import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import "bootstrap/dist/css/bootstrap.min.css";
import "@fortawesome/fontawesome-pro/css/all.min.css";
import "./index.scss";
import "./assets/css/index.css";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";
import App from "./App";
import { datadogLogs } from "@datadog/browser-logs";
import packageJson from "../package.json";

import { IntlProvider } from "react-intl";

import "@formatjs/intl-pluralrules/polyfill-locales";
import "@formatjs/intl-relativetimeformat/polyfill-locales";
import { BrowserRouter } from "react-router";
import { captureUtmOnArrival } from "./utils/UtmCapture";

// Capture-on-arrival: if the URL carries utm_* params, persist them to the shared
// ow_utm cookie before React mounts. Runs once per page load on every route
// (including /login), so a tagged email link landing anywhere is attributed.
captureUtmOnArrival();

const loadTranslations = async () => {
    const messages: any = {};
    try {
        messages.de = (await import("./translations/de.json")).default;
        messages.en = (await import("./translations/en.json")).default;
        messages.es = (await import("./translations/es.json")).default;
        messages.it = (await import("./translations/it.json")).default;
        messages.fr = (await import("./translations/fr.json")).default;
        messages.th = (await import("./translations/th.json")).default;
    } catch (error) {
        console.error("Error loading translations:", error);
        // Fallback to at least having English
        messages.en = {};
    }

    return messages;
};

// Only initialize DataDog in production
if (process.env.NODE_ENV === "production") {
    datadogLogs.init({
        clientToken: "pub75a1d63e5e32d1eb078112011cf18e80",
        forwardErrorsToLogs: true,
        sessionSampleRate: 100,
        service: "Web App",
        version: packageJson.version,
    });
} else {
    // In development, initialize with a silent configuration
    datadogLogs.init({
        clientToken: "dummy", // Required field but won't be used
        forwardErrorsToLogs: false,
        sessionSampleRate: 0, // Don't sample any sessions
        service: "Web App (dev)",
        version: packageJson.version,
        beforeSend: () => false, // Prevent any logs from being sent
    });
    console.log("DataDog logging disabled in development mode");
}

const container = document.getElementById("root");
const root = createRoot(container!);

// Load translations then render
loadTranslations().then((messages) => {
    const language = navigator.language.split(/[-_]/)[0];
    const hasLanguage = !!messages[language];

    root.render(
        <StrictMode>
            <IntlProvider locale={hasLanguage ? language : "en"} messages={hasLanguage ? messages[language] : messages.en}>
                <BrowserRouter>
                    <App />
                </BrowserRouter>
            </IntlProvider>
        </StrictMode>
    );
});
